import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import UserMenu from "../components/UserMenu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Settings,
  Upload,
  ArrowLeft,
  ArrowRightLeft,
  LogOut,
  Lock,
  Image as ImageIcon
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
});

function SafetyQRSettings({ user, onLogout }) {
  const navigate = useNavigate();
  const logoInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({
    base_url: "https://hi.faultreport.in/?code=",
    logo_filename: null
  });
  const [editingUrl, setEditingUrl] = useState(false);
  const [newBaseUrl, setNewBaseUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [sqftLocked, setSqftLocked] = useState(false);
  const [savingSqft, setSavingSqft] = useState(false);

  const hasPermission = (perm) => permissions.includes(perm);

  useEffect(() => {
    fetchPermissions();
    fetchSettings();
    fetchProductionSettings();
  }, []);

  const fetchProductionSettings = async () => {
    try {
      const r = await axios.get(`${API}/production/settings`, getAuthHeaders());
      setSqftLocked(!!r.data?.sqft_locked);
    } catch (_) { /* ignore */ }
  };

  const handleToggleSqftLock = async (value) => {
    setSavingSqft(true);
    setSqftLocked(value);
    try {
      await axios.put(`${API}/production/settings`, { sqft_locked: value }, getAuthHeaders());
      toast.success(value ? "Sq Ft fields locked (read-only, auto-calculated)" : "Sq Ft fields unlocked (editable)");
    } catch (error) {
      setSqftLocked(!value); // revert
      toast.error(error.response?.data?.detail || "Failed to update setting");
    } finally {
      setSavingSqft(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      const res = await axios.get(`${API}/auth/me/permissions`, getAuthHeaders());
      setPermissions(res.data.effective_permissions || []);
    } catch (error) {
      console.error("Failed to load permissions:", error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/qr/settings`, getAuthHeaders());
      setSettings(response.data);
      setNewBaseUrl(response.data.base_url);
    } catch (error) {
      console.error("Failed to load settings:", error);
      toast.error("Failed to load QR settings");
    } finally {
      setLoading(false);
    }
  };

  // Update base URL
  const handleUpdateUrl = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/qr/settings`, {
        base_url: newBaseUrl
      }, getAuthHeaders());
      
      setSettings({ ...settings, base_url: newBaseUrl });
      setEditingUrl(false);
      toast.success("Base URL updated successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update settings");
    } finally {
      setSaving(false);
    }
  };

  // Upload logo
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error("Please upload an image file");
      return;
    }
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/qr/settings/logo`, formData, {
        ...getAuthHeaders(),
        headers: {
          ...getAuthHeaders().headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setSettings({ ...settings, logo_filename: response.data.filename });
      toast.success("Logo uploaded successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload logo");
    }
    
    // Reset file input
    if (logoInputRef.current) {
      logoInputRef.current.value = "";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div data-testid="safety-qr-settings">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate("/safety")}
              variant="ghost"
              className="text-white hover:bg-white/10 -ml-2"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <Settings className="w-7 h-7" />
            <div>
              <h1 className="text-xl font-bold">QR Code Settings</h1>
              <p className="text-emerald-100 text-sm">Configure QR code generation settings</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {hasPermission("module.project") && (
              <Button
                onClick={() => navigate("/")}
                variant="outline"
                className="bg-white/10 hover:bg-white/20 text-white border-white/30"
              >
                <ArrowRightLeft className="w-4 h-4 mr-2" />
                Project Module
              </Button>
            )}
            <UserMenu user={user} onLogout={onLogout} variant="header" />
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-6">
        <div className="space-y-6">
          {/* Base URL Setting */}
          <div className="bg-white rounded-lg border shadow-sm p-6">
            <h3 className="font-semibold mb-2">QR Code Base URL</h3>
            <p className="text-sm text-gray-500 mb-4">
              The base URL that will be embedded in all QR codes. Product codes will be appended to this URL.
            </p>
            
            {editingUrl ? (
              <div className="space-y-3">
                <Input
                  value={newBaseUrl}
                  onChange={(e) => setNewBaseUrl(e.target.value)}
                  placeholder="https://example.com/?code="
                  data-testid="base-url-input"
                />
                <div className="flex gap-2">
                  <Button 
                    onClick={handleUpdateUrl} 
                    disabled={saving}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="save-url-btn"
                  >
                    {saving ? "Saving..." : "Save"}
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setEditingUrl(false);
                      setNewBaseUrl(settings.base_url);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <code className="bg-gray-100 px-3 py-2 rounded text-sm flex-1 mr-4 overflow-x-auto">
                  {settings.base_url}
                </code>
                <Button 
                  variant="outline" 
                  onClick={() => setEditingUrl(true)} 
                  data-testid="edit-url-btn"
                >
                  Edit
                </Button>
              </div>
            )}
            
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                <strong>Example QR URL:</strong>
                <br />
                <code className="text-xs">{settings.base_url}CP-2-20260115-01</code>
              </p>
            </div>
          </div>

          {/* Logo Setting */}
          <div className="bg-white rounded-lg border shadow-sm p-6">
            <h3 className="font-semibold mb-2">QR Code Logo</h3>
            <p className="text-sm text-gray-500 mb-4">
              Logo image that will be embedded in the center of all QR codes. Use a square image for best results.
            </p>
            
            <div className="flex items-center gap-6">
              {settings.logo_filename ? (
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-white overflow-hidden">
                    <img
                      src={`${API}/qr/settings/logo?t=${Date.now()}`}
                      alt="QR Logo"
                      className="max-w-full max-h-full object-contain"
                      onError={(e) => { 
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<span class="text-gray-400 text-xs">Failed</span>';
                      }}
                    />
                  </div>
                  <div>
                    <p className="font-medium">{settings.logo_filename}</p>
                    <p className="text-xs text-gray-500">Current logo</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
                    <ImageIcon className="w-8 h-8 text-gray-300" />
                  </div>
                  <div>
                    <p className="text-gray-500">No logo configured</p>
                    <p className="text-xs text-gray-400">QR codes will be generated without a logo</p>
                  </div>
                </div>
              )}
              
              <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
                data-testid="logo-file-input"
              />
              <Button 
                variant="outline" 
                onClick={() => logoInputRef.current?.click()}
                data-testid="upload-logo-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                {settings.logo_filename ? 'Change Logo' : 'Upload Logo'}
              </Button>
            </div>
            
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>Tip:</strong> Use a PNG image with a transparent or white background. 
                The logo will be scaled to approximately 30% of the QR code size.
              </p>
            </div>
          </div>

          {/* Production Report — Sq Ft Lock */}
          <div className="bg-white rounded-lg border shadow-sm p-6" data-testid="production-sqft-setting">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <Lock className="w-4 h-4 text-gray-500" /> Production Report — Total Sq Ft
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Total &amp; Rework Sq Ft are auto-calculated from the selected products (accepted qty × surface area, same
              formula as the BOM). Turn this on to lock those fields to read-only once you've verified the calculations are
              correct; turn it off to let staff override the values manually.
            </p>
            <div className="flex items-center justify-between rounded-lg border p-4">
              <div>
                <p className="font-medium text-sm">Lock Sq Ft fields (read-only)</p>
                <p className="text-xs text-gray-500">
                  {sqftLocked ? "Currently LOCKED — fields are auto-calculated and cannot be edited." : "Currently editable — auto-calculated but staff can override."}
                </p>
              </div>
              <Switch
                checked={sqftLocked}
                onCheckedChange={handleToggleSqftLock}
                disabled={savingSqft || !hasPermission("settings.manage")}
                data-testid="sqft-lock-toggle"
              />
            </div>
            {!hasPermission("settings.manage") && (
              <p className="text-xs text-amber-600 mt-2">Only admins can change this setting.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SafetyQRSettings;
